import React, { Fragment, PureComponent } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import i18n from "i18next";

import { Export } from "../../Common/Export/Export";
import { LoadsListHeader, LoadsListRow, loadsSearchColumns, loadsSortColumns } from "./List/Loads";
import { Panel, PanelHeader, PanelMobile } from "../../Layout/Panel";
import { ProductsListHeader, ProductsListRow, productsSearchColumns, productsSortColumns } from "./List/Products";
import { SortedList, SortedListPagination } from "../../Layout/SortedList";
import { exportOrder } from "../../../helpers/xlsxHelpers";
import { getCustomer, getOrderDocuments, getPurchaseOrder, getPurchaseOrderLoads, getPurchaseOrderOrderedProducts } from "../../../actions";
import { getIsLoadingSelector, getLoadsSelector, getOrderedProductsSelector, getPurchaseOrderSelector } from "../../../selectors/selectors";
import { withNavigationHistory } from "../../Common/Navigation/withNavigationHistory";
import Document from "../../Common/Document/Document";
import OrderPanelHeaderSummary from "./Panel/HeaderSummary";
import OrderPanelHeaderSummaryMobile from "./Panel/HeaderSummaryMobile";
import OrderPanelSubNav from "./SubNav";
import PanelTools from "./../../Layout/Panel/PanelTools";
import Representative from "./Representative/Representative";
import SidePanel from "../../Layout/Panel/SidePanel";
import ViewTypes from "../ViewTypes";

class OrderPage extends PureComponent {
  static propTypes = {
    getCustomer: PropTypes.func.isRequired,
    getOrderDocuments: PropTypes.func.isRequired,
    getPurchaseOrder: PropTypes.func.isRequired,
    getPurchaseOrderLoads: PropTypes.func.isRequired,
    getPurchaseOrderOrderedProducts: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    breadcrumbStack: PropTypes.array,
    customerId: PropTypes.string,
    customerPOId: PropTypes.string,
    isMobile: PropTypes.bool,
    loads: PropTypes.array,
    orderedProducts: PropTypes.array,
    purchaseOrder: PropTypes.object,
    userMetadata: PropTypes.object
  };

  static defaultProps = {
    breadcrumbStack: [],
    customerId: "",
    customerPOId: "",
    isMobile: false,
    orderedProducts: [],
    loads: [],
    purchaseOrder: {},
    userMetadata: {}
  };

  state = {
    isDocumentVisible: false,
    isRepresentativeVisible: false,
    isSummaryOpen: false,
    viewState: {
      isProductSelected: this.props.userMetadata.defaultOrderListView === ViewTypes.Products,
      loads: {
        currentPage: 1,
        openedRows: [],
        searchQuery: "",
        sort: {}
      },
      products: {
        currentPage: 1,
        openedRows: [],
        searchQuery: "",
        sort: {}
      }
    }
  };

  componentDidMount() {
    if (this.props.customerId) {
      this.props.getCustomer(this.props.customerId);
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.userMetadata.defaultOrderListView && !prevProps.userMetadata.defaultOrderListView) {
      this.toggleOrderView(this.props.userMetadata.defaultOrderListView === ViewTypes.Products);
    }
  }

  getPurchaseOrderDetails = units => {
    this.props.getPurchaseOrder(this.props.customerPOId, units);
    this.props.getPurchaseOrderOrderedProducts(this.props.customerPOId, units);
    this.props.getPurchaseOrderLoads(this.props.customerPOId, units);
  };

  handleChangeUnits = units => this.getPurchaseOrderDetails(units);

  handleGetDocuments = () => this.props.getOrderDocuments(this.props.customerPOId);

  handleShowDocumentButton = () => this.setState({ isDocumentVisible: true });

  handleHideDocumentButton = () => this.setState({ isDocumentVisible: false });

  handleShowRepresentativeButton = () => this.setState({ isRepresentativeVisible: true });

  handleHideRepresentativeButton = () => this.setState({ isRepresentativeVisible: false });

  handleSetViewState = item => this.setState({ viewState: { ...this.state.viewState, ...item } });

  handleSetSubnavViewState = item => {
    const { isProductSelected, products, loads } = this.state.viewState;
    const keyName = isProductSelected ? "products" : "loads";
    const value = isProductSelected ? products : loads;

    this.handleSetViewState({ [keyName]: { ...value, ...item } });
  };

  handleChangePagination = currentPage => this.handleSetSubnavViewState({ currentPage });

  handleChangeSort = sort => this.handleSetSubnavViewState({ sort });

  handleChangeOpenedRows = openedRows => this.handleSetSubnavViewState({ openedRows });

  toggleOrderView = isProductSelected => this.handleSetViewState({ isProductSelected });

  handleSearch = searchQuery => this.handleSetSubnavViewState({ searchQuery, currentPage: 1 });

  handleExportCSVClick = () => this.handleExportOrder(false);

  handleExportExcelClick = () => this.handleExportOrder(true);

  handleSummaryButtonClick = () => {
    this.setState({ isSummaryOpen: !this.state.isSummaryOpen });
  };

  handleExportOrder = isXLSX => {
    const { orderedProducts, loads, purchaseOrder, userMetadata } = this.props;
    exportOrder(purchaseOrder, orderedProducts, loads, userMetadata.dateDisplayFormat, isXLSX);
  };

  actions = [
    {
      icon: "file",
      label: i18n.t("purchaseOrder.documents"),
      onClick: this.handleShowDocumentButton
    },
    {
      icon: "person",
      label: i18n.t("representative.title"),
      onClick: this.handleShowRepresentativeButton,
      className: "--representative"
    },
    {
      icon: "table",
      key: "csv",
      label: i18n.t("export.csvFile"),
      onClick: this.handleExportCSVClick,
      showOnlyOnMobile: true
    },
    {
      icon: "table",
      key: "xlsx",
      label: i18n.t("export.excelFile"),
      onClick: this.handleExportExcelClick,
      showOnlyOnMobile: true
    }
  ];

  render() {
    const { isProductSelected, products: viewStateProducts = {}, loads: viewStateLoads = {} } = this.state.viewState;
    const { openedRows, currentPage, sort, searchQuery } = isProductSelected ? viewStateProducts : viewStateLoads;
    const { customerId, customerPOId, breadcrumbStack, isLoading, isMobile, loads, orderedProducts, purchaseOrder, userMetadata } = this.props;
    const { isDocumentVisible, isRepresentativeVisible, isSummaryOpen } = this.state;
    return (
      <Fragment>
        {isMobile ? (
          <PanelMobile className="order-page">
            <SidePanel position="right" isVisible={isDocumentVisible} panelVisibility={this.handleHideDocumentButton} isMobile={isMobile}>
              {isDocumentVisible ? (
                <Document
                  noResultTitle={i18n.t("table.noPODocumentsTitle")}
                  noResultMessage={i18n.t("table.noPODocumentsMessage")}
                  getDocuments={this.handleGetDocuments}
                  isLoading={isLoading}
                />
              ) : null}
            </SidePanel>
            <SidePanel position="right" isVisible={isRepresentativeVisible} panelVisibility={this.handleHideRepresentativeButton} isMobile={isMobile}>
              {isRepresentativeVisible ? <Representative customerPOId={customerPOId} isMobile={isMobile} /> : null}
            </SidePanel>
            <PanelHeader
              title={i18n.t("purchaseOrder.order")}
              id={purchaseOrder.customerPOId}
              actions={this.actions}
              customAction={<Export actions={this.actions.filter(x => x.showOnlyOnMobile)} />}
              isMobile={isMobile}
              summaryButtonClick={this.handleSummaryButtonClick}
              summaryIsLoading
            />

            <OrderPanelHeaderSummaryMobile
              purchaseOrder={purchaseOrder}
              isSummaryOpen={isSummaryOpen}
              summaryButtonClick={this.handleSummaryButtonClick}
            />

            <OrderPanelSubNav activeSubNav={isProductSelected ? ViewTypes.Products : ViewTypes.Loads} onToggleOrderView={this.toggleOrderView} />
            <PanelTools
              searchQuery={searchQuery}
              isSearchVisible
              onSearch={this.handleSearch}
              isUnitsOfMeasureVisible
              changeUnits={this.handleChangeUnits}
            />

            {isProductSelected && (
              <SortedList
                RowComponent={ProductsListRow}
                PaginationComponent={SortedListPagination}
                HeaderComponent={ProductsListHeader}
                data={orderedProducts}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                noResultMessage={i18n.t("table.noLoadsMessage")}
                noResultTitle={i18n.t("table.noLoadsTitle")}
                currentPage={currentPage}
                isLoading={isLoading}
                isMobile={isMobile}
                rowProps={{ customerId, customerPOId, breadcrumbStack, openedRows, onChangeOpenedRows: this.handleChangeOpenedRows, userMetadata }}
                searchQuery={searchQuery}
                searchValues={productsSearchColumns}
                sort={sort}
                sortValues={productsSortColumns}
              />
            )}

            {!isProductSelected && (
              <SortedList
                RowComponent={LoadsListRow}
                PaginationComponent={SortedListPagination}
                HeaderComponent={LoadsListHeader}
                data={loads}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                noResultMessage={i18n.t("table.noLoadsMessage")}
                noResultTitle={i18n.t("table.noLoadsTitle")}
                currentPage={currentPage}
                isLoading={isLoading}
                isMobile={isMobile}
                rowProps={{ customerId, customerPOId, breadcrumbStack, openedRows, onChangeOpenedRows: this.handleChangeOpenedRows, userMetadata }}
                searchQuery={searchQuery}
                searchValues={loadsSearchColumns}
                sort={sort}
                sortValues={loadsSortColumns}
              />
            )}
          </PanelMobile>
        ) : (
          <Panel className="order-page">
            <SidePanel position="right" isVisible={isDocumentVisible} panelVisibility={this.handleHideDocumentButton} isMobile={isMobile}>
              {isDocumentVisible ? (
                <Document
                  noResultTitle={i18n.t("table.noPODocumentsTitle")}
                  noResultMessage={i18n.t("table.noPODocumentsMessage")}
                  getDocuments={this.handleGetDocuments}
                  isLoading={isLoading}
                />
              ) : null}
            </SidePanel>
            <SidePanel position="right" isVisible={isRepresentativeVisible} panelVisibility={this.handleHideRepresentativeButton} isMobile={isMobile}>
              {isRepresentativeVisible ? <Representative customerPOId={customerPOId} isMobile={isMobile} /> : null}
            </SidePanel>
            <PanelHeader
              title={i18n.t("purchaseOrder.order")}
              id={purchaseOrder.customerPOId}
              actions={this.actions}
              customAction={<Export actions={this.actions.filter(x => x.showOnlyOnMobile)} />}
              isMobile={isMobile}
              summaryButtonClick={this.handleSummaryButtonClick}
              summaryIsLoading
            />
            <OrderPanelHeaderSummary purchaseOrder={purchaseOrder} />
            <OrderPanelSubNav activeSubNav={isProductSelected ? ViewTypes.Products : ViewTypes.Loads} onToggleOrderView={this.toggleOrderView} />
            <PanelTools
              searchQuery={searchQuery}
              isSearchVisible
              onSearch={this.handleSearch}
              isUnitsOfMeasureVisible
              changeUnits={this.handleChangeUnits}
            />

            {isProductSelected && (
              <SortedList
                RowComponent={ProductsListRow}
                PaginationComponent={SortedListPagination}
                HeaderComponent={ProductsListHeader}
                data={orderedProducts}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                noResultMessage={i18n.t("table.noLoadsMessage")}
                noResultTitle={i18n.t("table.noLoadsTitle")}
                currentPage={currentPage}
                isLoading={isLoading}
                rowProps={{ customerId, customerPOId, breadcrumbStack, openedRows, onChangeOpenedRows: this.handleChangeOpenedRows, userMetadata }}
                searchQuery={searchQuery}
                searchValues={productsSearchColumns}
                sort={sort}
                sortValues={productsSortColumns}
              />
            )}

            {!isProductSelected && (
              <SortedList
                RowComponent={LoadsListRow}
                PaginationComponent={SortedListPagination}
                HeaderComponent={LoadsListHeader}
                data={loads}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                noResultMessage={i18n.t("table.noLoadsMessage")}
                noResultTitle={i18n.t("table.noLoadsTitle")}
                currentPage={currentPage}
                isLoading={isLoading}
                rowProps={{ customerId, customerPOId, breadcrumbStack, openedRows, onChangeOpenedRows: this.handleChangeOpenedRows, userMetadata }}
                searchQuery={searchQuery}
                searchValues={loadsSearchColumns}
                sort={sort}
                sortValues={loadsSortColumns}
              />
            )}
          </Panel>
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = state => {
  const { customerPOId, customerId, breadcrumbStack } = state.app.routerParams;
  const actionsId = [getPurchaseOrder.id, getCustomer.id, getOrderDocuments.id, getPurchaseOrderLoads.id, getPurchaseOrderOrderedProducts.id];

  return {
    customerId,
    customerPOId,
    breadcrumbStack,
    isLoading: getIsLoadingSelector(state, actionsId),
    isMobile: state.app.isMobile,
    loads: getLoadsSelector(state),
    orderedProducts: getOrderedProductsSelector(state),
    purchaseOrder: getPurchaseOrderSelector(state, customerPOId),
    userMetadata: state.app.userMetadata
  };
};

export default connect(
  mapStateToProps,
  { getCustomer, getPurchaseOrder, getOrderDocuments, getPurchaseOrderLoads, getPurchaseOrderOrderedProducts }
)(withNavigationHistory(OrderPage));



// WEBPACK FOOTER //
// ./src/components/PurchaseOrder/OrderPage/OrderPage.js